/*
** $Id$
**
** Copyright 2018-2019 Port25 Solutions, Inc.
** All rights reserved.
*/


import React, { Component } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import Table from './Table';
import ViewOptions from './ViewOptions';
import PropTypes from 'prop-types';
import { updateComponent, getFromJson } from '../util/Utils'
import uuidv4 from 'uuid/v4';
import { LONG_REFRESH_INTERVAL } from '../constants'

function makeTableData(state, items, columns, showLastError, title) {
    let columnNames = Object.keys(columns)
    if (showLastError) {
        columnNames.push("Last Error")
    }
    return {
        items: {
            columns: columnNames,
            data: makeItems(state, items, columns),
        },
        title: title || getFromJson(state, ['data', 'data', 'title']),
        message: getFromJson(state, ['data', 'message'])
    }
}


function makeItems(state, items, columns) {
    let allItems = getFromJson(state, ['data', 'data', items], [])
    let result = []
    allItems.forEach((q) => {
        let lastError
        if (q.errors && q.errors.length > 0) {
            // Pick first error, since errors are always sorted (latest first).
            lastError = q.errors[0].time + ' ' + q.errors[0].text
        }
        let row = []
        Object.keys(columns).forEach((c) => {
            row.push(q[columns[c]])
        })
        row.push(lastError)
        result.push(row)
    })
    return result
}


/**
 * A listing page of web monitor that shows top queues, domains, vmtas or
 * jobs.
 */
export default class ListingPage extends Component {
    constructor(props) {
        super(props)
        this.updateListing = this.updateListing.bind(this)
        this.refresh = this.refresh.bind(this)
        this.refreshInterval = this.refreshInterval.bind(this)
        this.makeTableKey = this.makeTableKey.bind(this)
        this.state = {
            options: this.props.defaultPmtaOptions,
            key: this.makeTableKey()
        }
    }


    static propTypes = {
        /**
         * Name of this listing.  e.g. 'queue' for the queues page.
         */
        name: PropTypes.string.isRequired,

        /**
         * Default value for name.  e.g. '*' for the domains page.
         */
        defaultName: PropTypes.string.isRequired,

        /**
         * URL path relative to the webroot from where to get the listing.
         * e.g. '/queues' for queue listing page.
         */
        urlPath: PropTypes.string.isRequired,

        /**
         * Name of JSON object's key within the 'data' object that has the value
         * of an array of items for this listing page, e.g. if PowerMTA returns
         * queues in the JSON object data.queues, then use 'queues' as items.
         */
        items: PropTypes.string.isRequired,

        /**
         * Column names and the corresponding JSON object's keys from which to
         * obtain the values for each column, e.g. if PowerMTA returns each
         * queue as a JSON object such as
         *     {
         *         name: 'foo.com/vmta'
         *     }
         * then to show a column for name under a column called 'Queue Name',
         * set columns to:
         *     {
         *         'Queue Name': 'name'
         *     }
         * If each queue also contains an 'errors' array, then the first from
         * the list is shown as 'Last Error', a column that is automatically
         * added.
         */
        columns: PropTypes.object.isRequired,

        /**
         * What to call to update errors.
         */
        showError: PropTypes.func.isRequired,

        /**
         * How to format the columns.  No special formatting is done if
         * none is specified i.e. they are rendered as simple text.  The
         * object should contain the column names as keys and their
         * respective formatting functions as values.
         */
        columnFormatter: PropTypes.object,

        /**
         * Options to use while querying PowerMTA when the page is loaded
         * for the first time.
         */
        defaultPmtaOptions: PropTypes.object,

        /**
         * Whether to show the last error column or not.  If not specified,
         * defaults to false (i.e. to not show the last error column).
         */
        showLastError: PropTypes.bool,

        /**
         * Whether to show the view options or not.  If not specified,
         * defaults to false (i.e. to not show the view options).
         */
        showViewOptions: PropTypes.bool,

        /**
         * Title to use for the listing.  If not specified, an attempt is made
         * to parse title from within the response from PowerMTA.
         */
        title: PropTypes.string,

        /**
         * Sets how to refresh this component upon auto refresh.
         */
        setRefresh: PropTypes.func.isRequired,

        /**
         * Sets the refresh interval.
         */
        setRefreshInterval: PropTypes.func.isRequired
    };


    componentDidMount() {
        this.props.setRefresh(this.refresh)
        this.props.setRefreshInterval(this.refreshInterval)
        this.refresh() 
    }


    refreshInterval() {
        return LONG_REFRESH_INTERVAL
    }


    /**
     * Updates state to show listing for the given options.
     *
     * @param options
     *      options for updating the page.
     * @param resetListingTable
     *      whether to reset the listing table e.g. to go back to default sort order after
     *      having changed it by clicking a column header.
     */
    async updateListing(options, resetListingTable) {
        updateComponent(this, this.props.urlPath, options)
        this.setState({
            options: options,
            // When the "Update" button is clicked, a new sort order is in place, depending on
            // the "sort" option selected with radio buttons.  We reset the sort arrows in
            // column headers by changing the "key" so that react remounts the component.
            // 'react-bootstrap-table-next' does not provide a way to 'clear' the sort in a
            // simpler way.
            key: resetListingTable ? this.makeTableKey() : this.state.key
        })
    }


    makeTableKey() {
        return 'table-' + uuidv4();
    }


    refresh() {
        return this.updateListing(this.state.options)
    }


    render() {
        let table = makeTableData(this.state, this.props.items,
                                  this.props.columns, this.props.showLastError,
                                  this.props.title)
        let isForQueues = (this.props.urlPath === '/queues')

        return (
            <Container>
                <Row>
                    <Col md={12}>{
                        <Table columns={table.items.columns}
                               data={table.items.data}
                               caption={table.title}
                               columnFormatter={this.props.columnFormatter}
                               sort={true}
                               key={this.state.key}
                               id={this.state.key}/>
                    }</Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <div>
                            {table && table.message}
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>{
                        this.props.showViewOptions &&
                        <ViewOptions name={this.props.name}
                                     defaultName={this.props.defaultName}
                                     showPaused={isForQueues}
                                     showMode={isForQueues}
                                     showVmta={!isForQueues}
                                     onUpdate={this.updateListing}/>
                    }</Col>
                </Row>
            </Container>
        );
    }
}
